'use strict';

const MusicSequencer = require('./music-sequencer');

class FeedbackService {
  constructor() {
    this.enabled = true;
    this.audioContext = null;
    this.audioResumePromise = null;
    this.masterGain = null;
    this.musicGain = null;
    this.musicSequencer = null;
    this.musicTrack = null;
    this.musicPaused = false;
    this.mediaFallback = null;
    this.mediaFallbackUnlocked = false;
    this.webAudioFailed = false;
    this.fallbackToneCache = new Map();
    this.hapticSwitch = null;
    this.lastLaserAt = 0;
    this.lastCarrotAt = 0;
    this.lastBossDamageAt = 0;
  }

  setEnabled(enabled) {
    this.enabled = Boolean(enabled);
    if (!this.enabled) {
      if (this.musicSequencer) this.musicSequencer.setTrack(null);
      return;
    }
    this.syncMusic();
  }

  ensureAudioContext() {
    if (this.audioContext) return this.audioContext;
    try {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (AudioContextClass) {
        this.audioContext = new AudioContextClass();
        this.masterGain = this.audioContext.createGain();
        this.masterGain.gain.value = 0.9;
        this.masterGain.connect(this.audioContext.destination);
        this.musicGain = this.audioContext.createGain();
        this.musicGain.gain.value = 0.34;
        this.musicGain.connect(this.masterGain);
        this.musicSequencer = new MusicSequencer(this.audioContext, this.musicGain);
      }
    } catch (error) {
      this.audioContext = null;
      this.masterGain = null;
      this.webAudioFailed = true;
    }
    return this.audioContext;
  }

  unlockFromGesture() {
    if (!this.enabled) return Promise.resolve(false);
    this.unlockMediaFallback();
    const context = this.ensureAudioContext();
    if (!context) return Promise.resolve(this.mediaFallbackUnlocked);
    this.queueSilentWarmup(context);
    if (context.state !== 'suspended' || typeof context.resume !== 'function') {
      const ready = context.state !== 'closed';
      if (ready) this.syncMusic();
      return Promise.resolve(ready);
    }
    if (!this.audioResumePromise) {
      this.audioResumePromise = Promise.resolve(context.resume())
        .then(() => {
          const ready = context.state === 'running';
          this.webAudioFailed = !ready;
          if (ready) this.syncMusic();
          return ready;
        })
        .catch(() => {
          this.webAudioFailed = true;
          return false;
        })
        .finally(() => { this.audioResumePromise = null; });
    }
    return this.audioResumePromise;
  }

  unlock() {
    return this.unlockFromGesture();
  }

  setMusicTrack(track) {
    this.musicTrack = ['dodge', 'boss'].includes(track) ? track : null;
    this.musicPaused = false;
    this.syncMusic();
  }

  setMusicPaused(paused) {
    this.musicPaused = Boolean(paused);
    if (this.musicSequencer) this.musicSequencer.setPaused(this.musicPaused);
  }

  syncMusic() {
    if (!this.enabled) return;
    if (!this.musicTrack || this.musicPaused) {
      if (this.musicSequencer) {
        this.musicSequencer.setPaused(this.musicPaused);
        this.musicSequencer.setTrack(null);
      }
      return;
    }
    const context = this.ensureAudioContext();
    if (!context || !this.musicSequencer || context.state === 'closed') return;
    this.musicSequencer.setTrack(this.musicTrack);
    this.musicSequencer.setPaused(false);
  }

  queueSilentWarmup(context) {
    if (!context || typeof context.createOscillator !== 'function') return;
    try {
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.frequency.value = 1;
      gain.gain.setValueAtTime(0.0001, context.currentTime);
      oscillator.connect(gain);
      gain.connect(this.masterGain || context.destination);
      oscillator.start(context.currentTime);
      oscillator.stop(context.currentTime + 0.015);
    } catch (error) {
      this.webAudioFailed = true;
    }
  }

  playTone(frequency, duration, volume) {
    if (!this.enabled) return;
    const context = this.ensureAudioContext();
    if (!context || this.webAudioFailed || typeof context.createOscillator !== 'function') {
      this.playFallbackTone(frequency, duration, volume);
      return;
    }
    if (context.state === 'suspended') {
      this.unlockFromGesture().then((ready) => {
        if (!ready && this.enabled) this.playFallbackTone(frequency, duration, volume);
      });
    }
    this.playToneNow(frequency, duration, volume);
  }

  playToneNow(frequency, duration, volume) {
    const context = this.audioContext;
    if (!context || context.state === 'closed') return;
    try {
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.value = frequency;
      gain.gain.setValueAtTime(volume, context.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, context.currentTime + duration);
      oscillator.connect(gain);
      gain.connect(this.masterGain || context.destination);
      oscillator.start(context.currentTime);
      oscillator.stop(context.currentTime + duration);
    } catch (error) {
      this.webAudioFailed = true;
      this.playFallbackTone(frequency, duration, volume);
    }
  }

  createFallbackTone(frequency, duration, volume) {
    if (typeof btoa !== 'function') return '';
    const sampleRate = 12000;
    const sampleCount = Math.max(1, Math.floor(sampleRate * Math.min(0.35, duration)));
    const bytes = new Uint8Array(44 + sampleCount * 2);
    const view = new DataView(bytes.buffer);
    const write = (offset, text) => {
      for (let index = 0; index < text.length; index += 1) view.setUint8(offset + index, text.charCodeAt(index));
    };
    write(0, 'RIFF'); view.setUint32(4, 36 + sampleCount * 2, true); write(8, 'WAVE');
    write(12, 'fmt '); view.setUint32(16, 16, true); view.setUint16(20, 1, true);
    view.setUint16(22, 1, true); view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * 2, true); view.setUint16(32, 2, true); view.setUint16(34, 16, true);
    write(36, 'data'); view.setUint32(40, sampleCount * 2, true);
    const amplitude = Math.min(0.42, Math.max(0.08, Number(volume) * 2.8));
    for (let index = 0; index < sampleCount; index += 1) {
      const envelope = Math.max(0, 1 - index / sampleCount);
      const sample = Math.sin((Math.PI * 2 * frequency * index) / sampleRate) * amplitude * envelope;
      view.setInt16(44 + index * 2, Math.round(sample * 32767), true);
    }
    let binary = '';
    for (let index = 0; index < bytes.length; index += 1) binary += String.fromCharCode(bytes[index]);
    return `data:audio/wav;base64,${btoa(binary)}`;
  }

  ensureMediaFallback() {
    if (this.mediaFallback) return this.mediaFallback;
    try {
      const AudioClass = typeof Audio !== 'undefined' ? Audio : window.Audio;
      if (!AudioClass) return null;
      const media = new AudioClass();
      media.preload = 'auto';
      media.setAttribute('playsinline', '');
      media.setAttribute('webkit-playsinline', '');
      media.volume = 1;
      this.mediaFallback = media;
      return media;
    } catch (error) {
      return null;
    }
  }

  unlockMediaFallback() {
    if (this.mediaFallbackUnlocked) return;
    const media = this.ensureMediaFallback();
    if (!media) return;
    const silentTone = this.createFallbackTone(1, 0.02, 0);
    if (!silentTone) return;
    try {
      media.src = silentTone;
      media.currentTime = 0;
      const result = media.play();
      this.mediaFallbackUnlocked = true;
      if (result && typeof result.catch === 'function') {
        result.catch(() => { this.mediaFallbackUnlocked = false; });
      }
    } catch (error) {
      this.mediaFallbackUnlocked = false;
    }
  }

  playFallbackTone(frequency, duration, volume) {
    if (!this.enabled) return;
    const media = this.ensureMediaFallback();
    if (!media) return;
    const key = `${Math.round(frequency)}:${Math.round(duration * 1000)}:${Math.round(volume * 100)}`;
    let source = this.fallbackToneCache.get(key);
    if (!source) {
      source = this.createFallbackTone(frequency, duration, volume);
      if (!source) return;
      this.fallbackToneCache.set(key, source);
    }
    try {
      media.src = source;
      media.currentTime = 0;
      const result = media.play();
      if (result && typeof result.catch === 'function') result.catch(() => null);
    } catch (error) {
      // Audio is optional and must degrade silently.
    }
  }

  ensureHapticSwitch() {
    if (this.hapticSwitch && this.hapticSwitch.isConnected) return this.hapticSwitch;
    if (typeof document === 'undefined' || !document.body) return null;
    const input = document.createElement('input');
    input.type = 'checkbox';
    input.setAttribute('switch', '');
    input.setAttribute('aria-hidden', 'true');
    input.tabIndex = -1;
    input.style.cssText = 'position:fixed;left:-100px;top:-100px;width:1px;height:1px;opacity:0;pointer-events:none;';
    document.body.appendChild(input);
    this.hapticSwitch = input;
    return input;
  }

  switchHaptic(type) {
    const input = this.ensureHapticSwitch();
    if (!input || typeof input.click !== 'function') return false;
    try {
      input.click();
      if (type === 'long') setTimeout(() => input.click(), 70);
      return true;
    } catch (error) {
      return false;
    }
  }

  vibrate(type) {
    if (!this.enabled) return;
    try {
      if (typeof navigator.vibrate === 'function') {
        const accepted = navigator.vibrate(type === 'long' ? 180 : 35);
        if (accepted !== false) return;
      }
      this.switchHaptic(type);
    } catch (error) {
      this.switchHaptic(type);
    }
  }

  tap() {
    this.playTone(560, 0.065, 0.12);
    this.vibrate('short');
  }

  preview() {
    if (!this.enabled) return;
    this.playTone(620, 0.12, 0.15);
    setTimeout(() => this.playTone(920, 0.18, 0.14), 105);
    this.vibrate('short');
  }

  hit() {
    this.playTone(130, 0.22, 0.16);
    this.vibrate('long');
  }

  success() {
    this.playTone(760, 0.18, 0.14);
    this.vibrate('short');
    setTimeout(() => this.playTone(1040, 0.22, 0.13), 130);
  }

  now() {
    return typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();
  }

  transform() {
    this.playTone(330, 0.12, 0.12);
    setTimeout(() => this.playTone(660, 0.2, 0.13), 90);
    this.vibrate('long');
  }

  laser() {
    const now = this.now();
    if (now - this.lastLaserAt < 80) return;
    this.lastLaserAt = now;
    this.playTone(920, 0.04, 0.045);
  }

  carrot() {
    const now = this.now();
    if (now - this.lastCarrotAt < 120) return;
    this.lastCarrotAt = now;
    this.playTone(210, 0.08, 0.065);
  }

  playerDamage() {
    this.playTone(105, 0.24, 0.18);
    this.vibrate('long');
  }

  bossDamage() {
    const now = this.now();
    if (now - this.lastBossDamageAt < 60) return;
    this.lastBossDamageAt = now;
    this.playTone(480, 0.04, 0.05);
  }

  bossDefeated() {
    this.playTone(520, 0.16, 0.14);
    setTimeout(() => this.playTone(780, 0.18, 0.14), 110);
    setTimeout(() => this.playTone(1120, 0.28, 0.13), 220);
    this.vibrate('long');
  }

  destroy() {
    if (this.hapticSwitch && this.hapticSwitch.remove) this.hapticSwitch.remove();
    this.hapticSwitch = null;
    if (this.musicSequencer) this.musicSequencer.destroy();
    if (this.audioContext && typeof this.audioContext.close === 'function') {
      this.audioContext.close().catch(() => null);
    }
    if (this.mediaFallback) {
      try { this.mediaFallback.pause(); this.mediaFallback.removeAttribute('src'); } catch (error) { /* no-op */ }
    }
    this.audioContext = null;
    this.audioResumePromise = null;
    this.masterGain = null;
    this.musicGain = null;
    this.musicSequencer = null;
    this.musicTrack = null;
    this.musicPaused = false;
    this.mediaFallback = null;
    this.mediaFallbackUnlocked = false;
    this.webAudioFailed = false;
    this.fallbackToneCache.clear();
  }
}

module.exports = FeedbackService;
